<template>
  <div :class="classes">
    <div v-if="showParents">
      <site-display :value="site" v-if="site" :show-link-icon="showLinkIcon" />
      <building-display
        :value="building"
        v-if="building"
        :show-link-icon="showLinkIcon"
      />
    </div>
    <div v-if="showParents || showRoom">
      <room-display
        :value="room"
        v-if="room"
        :show-link-icon="showLinkIcon"
        show-floor
      />
    </div>
    <slot name="pre-icon" />
    <b-icon v-if="showIcon && icon" :icon="icon" :size="size" />
    <span v-if="iconPosition === 'right'">
      <slot name="pre-name" />
      {{ value.$displayString }}
      <span
        v-if="showTrainingLevel"
        :class="'has-text-weight-bold ' + value.$trainingLevelBuefyClass"
      >
        ({{ value.$trainingLevel }})
      </span>
    </span>
    <span v-if="value.floor">Floor: {{ value.floor }}</span>
    <b-tooltip
      :active="showTooltip && !showTempText"
      v-if="showTempIcon && tempIcon"
      :label="'Water temperature: ' + temperature"
      :type="iconColor"
      :position="tooltipPosition"
    >
      <div v-if="temperature === 'mixed' && showTempIcon && !showTempText">
        <b-icon :icon="iconFor('hot')" type="is-hot" :size="size" />
        <b-icon :icon="iconFor('cold')" type="is-cold" :size="size" />
      </div>
      <div v-else-if="showTempIcon && !showTempText" >
        <b-icon
          v-if="showTempIcon"
          :icon="tempIcon"
          :type="iconColor"
          :size="size"
        />
      </div>
      <div v-else-if="showTempText">
        ({{temperature||"temperature not set"}})
      </div>
    </b-tooltip>
    <span v-if="iconPosition === 'left'">
      <slot name="pre-name" />
      {{ value.$displayString }}
      <span
        v-if="showTrainingLevel"
        :class="'has-text-weight-bold ' + value.$trainingLevelBuefyClass"
      >
        ({{ value.$trainingLevel }})
      </span>
    </span>
    <span v-if="showLinkIcon" :class="{'is-pulled-right mr-3':!linkIconNoPull}" >
      <b-icon
        :icon="linkIcon"
        class="pointer"
        type="is-link"
        @click.native="
          $router.push(getLinkFunction(value));
          $event.preventDefault();
        "
      />
    </span>
    <slot name="end" />
  </div>
</template>

<script>
import { get } from "lodash";
import iconFor from "../../icons";
import BuildingDisplay from "./BuildingDisplay";
import RoomDisplay from "./RoomDisplay";
import SiteDisplay from "./SiteDisplay";
import { getObjectInArrayById } from "../../scripts/filterHelpers";

const temperatureColors = {
  hot: "is-hot",
  mixed: "is-mixed",
  cold: "is-cold",
};

export default {
  name: "Source",
  components: { BuildingDisplay, RoomDisplay, SiteDisplay },
  props: {
    linkIconNoPull: {
      type: Boolean,
      default: false
    },
    showTempText: {
      type: Boolean,
      default: false,
    },
    showTooltip: {
      type: Boolean,
      default: true,
    },
    showTrainingLevel: {
      type: Boolean,
      default: false,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    showTempIcon: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: "",
    },
    value: {
      type: Object,
      required: true,
    },
    showParents: {
      type: Boolean,
      default: false,
    },
    showRoom: {
      type: Boolean,
      default: false,
    },
    iconPosition: {
      type: String,
      default: "right",
      validator(value) {
        return ["right", "left"].includes(value);
      },
    },
    tooltipPosition: {
      type: String,
      default: "is-right",
      validator(value) {
        return ["is-right", "is-left", "is-top", "is-bottom"].includes(value);
      },
    },
    showLinkIcon: {
      type: Boolean,
      default: false,
    },
    linkIcon: {
      type: String,
      default: iconFor("update"),
    },
    getLinkFunction: {
      type: Function,
      default: function (src) {
        return this.$router.match({
          name: "administration:view-source",
          params: { id: src.id },
          query: {
            loadScope: src.id_str
              ? src.id_str.split("|").slice(0, 5).join("|")
              : null,
          },
        });
      },
    },
    classes: {
      type: String,
      default: "",
    },
  },
  data() {
    return {};
  },
  methods: {
    iconFor,
  },
  computed: {
    temperature() {
      return get(this.value, "temperature", null);
    },
    appliance() {
      return get(this.value, "appliance.type", null);
    },
    icon() {
      let iconValue = this.value.fixture
        ? this.value.fixture
        : "unknownSourceType";
      return iconFor(iconValue);
    },
    tempIcon() {
      return iconFor(this.temperature);
    },
    iconColor() {
      return get(temperatureColors, this.temperature, null);
    },
    site() {
      let value = this.value.site
        ? getObjectInArrayById(
            this.$store.state.sites,
            this.value.site,
            null
          )
        : null;
      return value;
    },
    room() {
      let value = this.value.room
        ? getObjectInArrayById(
            this.$store.state.rooms,
            this.value.room,
            null
          )
        : null;
      return value;
    },
    building() {
      let value = this.value.building
        ? getObjectInArrayById(
            this.$store.state.buildings,
            this.value.building,
            null
          )
        : null;
      return value;
    },
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

.hovering {
  background-color: deepskyblue;
}
</style>
